import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  getEscrowDetail, 
  releaseEscrowFunds, 
  refundEscrowFunds,
  addEscrowNotes,
  updateEscrowStatus,
  adjustEscrowFees,
  formatEscrowAmount
} from '../../services/api';
import EscrowDetailHeader from '../../components/escrows/EscrowDetailHeader';
import EscrowDetailContent from '../../components/escrows/EscrowDetailContent';
import EscrowDetailActions from '../../components/escrows/EscrowDetailActions';
import ActionModal from '../../components/escrows/ActionModal';

function EscrowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [escrow, setEscrow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [actionModal, setActionModal] = useState(null);
  const [modalData, setModalData] = useState({});
  const [notes, setNotes] = useState('');
  const [history, setHistory] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [relatedTransactions, setRelatedTransactions] = useState([]);

  useEffect(() => {
    if (id) {
      fetchEscrowDetail();
    }
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    if (action && ['release', 'refund'].includes(action)) {
      setActionModal(action);
    }
  }, [location.search]);

  const fetchEscrowDetail = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getEscrowDetail(id);
      if (response.success) {
        const escrowData = response.data;
        setEscrow(escrowData);
        setHistory(escrowData.history || []);
        setTimeline(escrowData.timeline || []);
        setRelatedTransactions(escrowData.transactions || []);
        
        if (escrowData.admin_notes) {
          setNotes(escrowData.admin_notes);
        }
      } else {
        setError(response.message || 'Failed to load escrow details');
      }
    } catch (err) {
      setError('Failed to load escrow details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseFunds = async () => {
    try {
      const releaseAmount = parseFloat(modalData.amount) || escrow.amount;
      const notes = modalData.notes || '';
      const force = modalData.force || false;

      const response = await releaseEscrowFunds(id, releaseAmount, notes, force);
      if (response.success) {
        setSuccessMessage(`Released ${formatEscrowAmount(releaseAmount)} to seller successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setActionModal(null);
        fetchEscrowDetail();
      } else {
        throw new Error(response.message || 'Release failed');
      }
    } catch (err) {
      setError('Failed to release funds: ' + err.message);
    }
  };

  const handleRefundFunds = async () => {
    try {
      const refundAmount = parseFloat(modalData.amount) || escrow.amount;
      const notes = modalData.notes || '';
      const force = modalData.force || false;

      const response = await refundEscrowFunds(id, refundAmount, notes, force);
      if (response.success) {
        setSuccessMessage(`Refunded ${formatEscrowAmount(refundAmount)} to buyer successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setActionModal(null);
        fetchEscrowDetail();
      } else {
        throw new Error(response.message || 'Refund failed');
      }
    } catch (err) {
      setError('Failed to refund funds: ' + err.message);
    }
  };

  const handleAddNotes = async () => {
    try {
      if (!notes.trim()) {
        setError('Notes cannot be empty');
        return;
      }

      const response = await addEscrowNotes(id, notes);
      if (response.success) {
        setSuccessMessage('Notes added successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setActionModal(null);
        fetchEscrowDetail();
      } else {
        throw new Error(response.message || 'Failed to add notes');
      }
    } catch (err) {
      setError('Failed to add notes: ' + err.message);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      const response = await updateEscrowStatus(id, modalData.status, modalData.notes || '');
      if (response.success) {
        setSuccessMessage(`Status updated to ${modalData.status} successfully!`);
        setTimeout(() => setSuccessMessage(''), 3000);
        setActionModal(null);
        fetchEscrowDetail();
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (err) {
      setError('Failed to update status: ' + err.message);
    }
  };

  const handleAdjustFees = async () => {
    try {
      const response = await adjustEscrowFees(
        id,
        modalData.platform_fee,
        modalData.paystack_fee,
        modalData.buyer_share,
        modalData.seller_share,
        modalData.seller_receives,
        modalData.notes || ''
      );
      if (response.success) {
        setSuccessMessage('Fees adjusted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        setActionModal(null);
        fetchEscrowDetail();
      } else {
        throw new Error(response.message || 'Failed to adjust fees');
      }
    } catch (err) {
      setError('Failed to adjust fees: ' + err.message);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-4 py-2 text-sm font-medium rounded-full flex items-center justify-center";
    
    const statusConfig = {
      'pending': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending', icon: '⏳' },
      'held': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Held', icon: '⏳' },
      'released': { bg: 'bg-green-100', text: 'text-green-800', label: 'Released', icon: '✅' },
      'refunded': { bg: 'bg-red-100', text: 'text-red-800', label: 'Refunded', icon: '❌' },
      'disputed': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Disputed', icon: '⚠️' },
      'cancelled': { bg: 'bg-gray-200', text: 'text-gray-700', label: 'Cancelled', icon: '❌' }
    };

    const config = statusConfig[status] || { bg: 'bg-gray-100', text: 'text-gray-800', label: status, icon: '?' };
    
    return (
      <span className={`${baseClasses} ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionButtons = () => {
    if (!escrow) return [];

    const actions = [];

    if (['held', 'disputed'].includes(escrow.status)) {
      actions.push(
        {
          label: 'Release to Seller',
          icon: '📈',
          color: 'bg-green-500 hover:bg-green-600',
          onClick: () => {
            setModalData({
              amount: escrow.seller_receives_net || escrow.amount,
              notes: ''
            });
            setActionModal('release');
          }
        },
        {
          label: 'Refund to Buyer',
          icon: '📉',
          color: 'bg-red-500 hover:bg-red-600',
          onClick: () => {
            setModalData({
              amount: escrow.amount,
              notes: ''
            });
            setActionModal('refund');
          }
        }
      );
    }

    if (escrow.dispute_id && escrow.dispute_status) {
      actions.push({
        label: 'Resolve Dispute',
        icon: '⚖️',
        color: 'bg-orange-500 hover:bg-orange-600',
        onClick: () => navigate(`/admin/escrows/disputes/${escrow.dispute_id}`)
      });
    }

    actions.push(
      {
        label: 'Add Notes',
        icon: '📝',
        color: 'bg-blue-500 hover:bg-blue-600',
        onClick: () => {
          setModalData({ notes: '' });
          setActionModal('notes');
        }
      },
      {
        label: 'Change Status',
        icon: '🔄',
        color: 'bg-purple-500 hover:bg-purple-600',
        onClick: () => {
          setModalData({ status: escrow.status, notes: '' });
          setActionModal('status');
        }
      },
      {
        label: 'Adjust Fees',
        icon: '💰',
        color: 'bg-indigo-500 hover:bg-indigo-600',
        onClick: () => {
          setModalData({
            platform_fee: escrow.platform_fee_total,
            paystack_fee: escrow.paystack_fee_total,
            buyer_share: escrow.buyer_fee_share,
            seller_share: escrow.seller_fee_share,
            seller_receives: escrow.seller_receives_net,
            notes: ''
          });
          setActionModal('fees');
        }
      }
    );

    return actions;
  };

  if (loading && !escrow) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        {/* Loading skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-700 rounded w-64 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-1/4 mb-4"></div>
                  <div className="space-y-3">
                    {[...Array(4)].map((_, j) => (
                      <div key={j} className="flex justify-between">
                        <div className="h-4 bg-gray-700 rounded w-1/3"></div>
                        <div className="h-4 bg-gray-700 rounded w-1/4"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="bg-gray-800 rounded-xl border border-gray-700 p-6">
                  <div className="h-6 bg-gray-700 rounded w-1/3 mb-4"></div>
                  <div className="space-y-2">
                    {[...Array(3)].map((_, j) => (
                      <div key={j} className="h-4 bg-gray-700 rounded w-full"></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!escrow && !loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 text-8xl mb-6">💰</div>
          <h3 className="text-2xl font-bold text-white mb-2">Escrow Not Found</h3>
          <p className="text-gray-400 mb-6">The escrow you're looking for doesn't exist or has been removed.</p>
          <button
            onClick={() => navigate('/admin/escrows')}
            className="px-6 py-3 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-colors font-medium flex items-center mx-auto hover:scale-[1.02]"
          >
            <span className="mr-2">←</span>
            Back to Escrows
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header Component */}
      <EscrowDetailHeader
        escrow={escrow}
        formatDate={formatDate}
        getStatusBadge={getStatusBadge}
        onNavigateBack={() => navigate('/admin/escrows')}
        onRefresh={fetchEscrowDetail}
        onViewOrder={() => navigate(`/admin/orders/${escrow.order_id}`)}
      />

      {/* Success & Error Messages */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <span className="text-green-400 text-xl">✅</span>
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <span className="text-red-400 text-xl">⚠️</span>
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Action Buttons Component */}
      <EscrowDetailActions
        actions={getActionButtons()}
        escrow={escrow}
      />

      {/* Main Content Component */}
      <EscrowDetailContent
        escrow={escrow}
        notes={notes}
        setNotes={setNotes}
        timeline={timeline}
        relatedTransactions={relatedTransactions}
        formatDate={formatDate}
        formatEscrowAmount={formatEscrowAmount}
      />

      {/* Action Modal */}
      {actionModal && (
        <ActionModal
          actionModal={actionModal}
          setActionModal={setActionModal}
          modalData={modalData}
          setModalData={setModalData}
          notes={notes}
          setNotes={setNotes}
          escrow={escrow}
          formatEscrowAmount={formatEscrowAmount}
          onRelease={handleReleaseFunds}
          onRefund={handleRefundFunds}
          onAddNotes={handleAddNotes}
          onUpdateStatus={handleUpdateStatus}
          onAdjustFees={handleAdjustFees}
        />
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default EscrowDetail;